/**
 * AttorneyPackageView - Secure Package Access Page
 * Allows attorneys to view their case package via secure link
 * Phase 6: Report Pipeline & Delivery
 */

import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Scale,
  FileText,
  DollarSign,
  Shield,
  Download,
  AlertCircle,
  Loader2,
  CheckCircle2,
  TrendingUp,
  ChevronRight,
  ChevronDown,
  Clock,
  Users,
  Lock,
  ExternalLink
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

interface PackageData {
  id: string;
  report_id: string;
  generated_at: string;
  status: string;
  case_name: string;
  client_name: string;
  event_type: string;
  executive_summary: string;
  settlement_calculation: {
    total: number;
    breakdown?: Record<string, number>;
    methodology?: string;
    confidence?: number;
    confidence_interval_low?: number;
    confidence_interval_high?: number;
  };
  case_strength_report: {
    score: number;
    vulnerabilities_total?: number;
    vulnerabilities_addressed?: number;
    evidence_completeness?: number;
    blocking_issues?: string[];
  };
  vulnerability_matrix: Array<{
    vulnerability_id: string;
    vulnerability: any;
    attack_vector: string;
    strategy_a?: any;
    strategy_b?: any;
    strategy_c?: any;
    recommended?: string;
  }>;
  document_index: Array<{
    doc_id: string;
    filename: string;
    doc_type: string;
    uploaded_at: string;
    page_count?: number;
  }>;
  confidence_scores: {
    data_extraction?: number;
    forensic_calculation?: number;
    case_strength?: number;
    overall?: number;
  };
  methodology_notes: string;
  total_documents: number;
  total_transactions: number;
  pdf_url?: string;
  version: number;
}

export default function AttorneyPackageView() {
  const { token } = useParams<{ token: string }>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [packageData, setPackageData] = useState<PackageData | null>(null);
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    summary: true,
    settlement: true,
    strength: false,
    defense: false,
    documents: false,
    methodology: false
  });
  const [downloading, setDownloading] = useState(false);
  const [downloadingDocx, setDownloadingDocx] = useState(false);

  useEffect(() => {
    if (token) {
      fetchPackage();
    }
  }, [token]);

  const fetchPackage = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${BACKEND_URL}/api/v1/packages/access/${token}/package`);

      if (!response.ok) {
        if (response.status === 401) {
          setError('This link has expired or is invalid. Please contact your ClaimSync representative.');
        } else {
          setError('Unable to load the package. Please try again.');
        }
        return;
      }

      const data = await response.json();
      setPackageData(data);
    } catch (err) {
      console.error('Error fetching package:', err);
      setError('Unable to connect to the server. Please check your internet connection.');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async () => {
    if (!token || downloading) return;

    setDownloading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/v1/packages/access/${token}/download`, {
        method: 'POST'
      });

      const data = await response.json();
      if (data.pdf_url) {
        window.open(data.pdf_url, '_blank');
      } else {
        toast.error('PDF download is not available for this package.');
      }
    } catch (err) {
      console.error('Download error:', err);
      toast.error('Failed to download the package.');
    } finally {
      setDownloading(false);
    }
  };

  const handleDocxDownload = async () => {
    if (!packageData || downloadingDocx) return;

    setDownloadingDocx(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/v1/packages/${packageData.id}/docx`);

      if (!response.ok) {
        throw new Error('DOCX download failed');
      }

      const blob = await response.blob();
      const contentDisposition = response.headers.get('content-disposition');
      let filename = `Attorney_Package_${packageData.case_name || packageData.id}.docx`;

      if (contentDisposition) {
        const match = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (match && match[1]) {
          filename = match[1].replace(/['"]/g, '');
        }
      }

      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('DOCX download error:', err);
      toast.error('Failed to download the DOCX package.');
    } finally {
      setDownloadingDocx(false);
    }
  };

  const toggleSection = (section: string) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatDate = (dateStr: string): string => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-blue-600 animate-spin mx-auto" />
          <p className="mt-4 text-slate-600">Loading your package...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Lock className="w-8 h-8 text-red-500" />
          </div>
          <h2 className="text-xl font-bold text-slate-800 mb-2">Access Error</h2>
          <p className="text-slate-600 mb-6">{error}</p>
          <div className="text-sm text-slate-500">
            <p>If you believe this is an error, please contact:</p>
            <a href="mailto:support@claimsync.io" className="text-blue-600 hover:underline">support@claimsync.io</a>
          </div>
        </div>
      </div>
    );
  }

  if (!packageData) return null;

  const { case_strength_report, settlement_calculation, vulnerability_matrix, document_index } = packageData;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-blue-700 rounded-lg flex items-center justify-center">
              <Scale className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="font-bold text-slate-800">ClaimSync</h1>
              <p className="text-xs text-slate-500">Attorney Package</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleDownload}
              disabled={downloading}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition disabled:opacity-50"
            >
              {downloading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Download className="w-4 h-4" />
              )}
              Download PDF
            </button>
            <button
              onClick={handleDocxDownload}
              disabled={downloadingDocx}
              className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition disabled:opacity-50"
            >
              {downloadingDocx ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <FileText className="w-4 h-4" />
              )}
              Download DOCX
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-8">
        {/* Case Header */}
        <div className="bg-white rounded-2xl shadow-sm p-6 mb-6">
          <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
            <div>
              <h2 className="text-2xl font-bold text-slate-800">{packageData.case_name}</h2>
              <p className="text-slate-600">{packageData.event_type}</p>
            </div>
            <div className="flex items-center gap-2 px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm">
              <CheckCircle2 className="w-4 h-4" />
              Analysis Complete
            </div>
          </div>

          {/* Key Metrics */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-slate-50 rounded-xl p-4">
              <p className="text-sm text-slate-500 mb-1">Calculated Loss</p>
              <p className="text-2xl font-bold text-blue-600">
                {formatCurrency(settlement_calculation?.total || 0)}
              </p>
            </div>
            <div className="bg-slate-50 rounded-xl p-4">
              <p className="text-sm text-slate-500 mb-1">Case Strength</p>
              <div className="flex items-center gap-2">
                <p className="text-2xl font-bold text-slate-800">
                  {case_strength_report?.score || 0}/100
                </p>
                <TrendingUp className="w-5 h-5 text-green-500" />
              </div>
            </div>
            <div className="bg-slate-50 rounded-xl p-4">
              <p className="text-sm text-slate-500 mb-1">Defense Points</p>
              <p className="text-2xl font-bold text-amber-600">
                {vulnerability_matrix?.length || 0}
              </p>
            </div>
            <div className="bg-slate-50 rounded-xl p-4">
              <p className="text-sm text-slate-500 mb-1">Documents</p>
              <p className="text-2xl font-bold text-slate-800">
                {packageData.total_documents}
              </p>
            </div>
          </div>
        </div>

        {/* Executive Summary */}
        <Section
          title="Executive Summary"
          icon={<FileText className="w-5 h-5" />}
          expanded={expandedSections.summary}
          onToggle={() => toggleSection('summary')}
        >
          <div className="prose prose-slate max-w-none">
            {packageData.executive_summary.split('\n').map((paragraph, index) => (
              <p key={index} className="text-slate-600">{paragraph}</p>
            ))}
          </div>
        </Section>

        {/* Settlement Calculation */}
        <Section
          title="Settlement Calculation"
          icon={<DollarSign className="w-5 h-5" />}
          expanded={expandedSections.settlement}
          onToggle={() => toggleSection('settlement')}
        >
          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-blue-50 rounded-xl">
              <span className="font-medium text-slate-700">Total Calculated Loss</span>
              <span className="text-2xl font-bold text-blue-600">
                {formatCurrency(settlement_calculation?.total || 0)}
              </span>
            </div>

            {settlement_calculation?.breakdown && Object.keys(settlement_calculation.breakdown).length > 0 && (
              <div className="border rounded-xl divide-y">
                {Object.entries(settlement_calculation.breakdown).map(([key, value]) => (
                  <div key={key} className="flex items-center justify-between p-3">
                    <span className="text-slate-600 capitalize">{key.replace(/_/g, ' ')}</span>
                    <span className="font-medium text-slate-800">{formatCurrency(value)}</span>
                  </div>
                ))}
              </div>
            )}

            {settlement_calculation?.confidence_interval_low && settlement_calculation?.confidence_interval_high && (
              <div className="p-4 bg-slate-50 rounded-xl">
                <p className="text-sm text-slate-500 mb-2">Confidence Interval (95%)</p>
                <p className="text-slate-700">
                  {formatCurrency(settlement_calculation.confidence_interval_low)} — {formatCurrency(settlement_calculation.confidence_interval_high)}
                </p>
              </div>
            )}

            <div className="text-sm text-slate-500">
              <p>Methodology: {settlement_calculation?.methodology || 'BI'}</p>
              <p>Calculation Confidence: {((settlement_calculation?.confidence || 0) * 100).toFixed(0)}%</p>
            </div>
          </div>
        </Section>

        {/* Case Strength */}
        <Section
          title="Case Strength Assessment"
          icon={<TrendingUp className="w-5 h-5" />}
          expanded={expandedSections.strength}
          onToggle={() => toggleSection('strength')}
        >
          <div className="space-y-4">
            {/* Score Gauge */}
            <div className="flex items-center gap-6">
              <div className="relative w-32 h-32">
                <svg className="w-full h-full transform -rotate-90" viewBox="0 0 100 100">
                  <circle
                    cx="50"
                    cy="50"
                    r="42"
                    stroke="#e2e8f0"
                    strokeWidth="12"
                    fill="none"
                  />
                  <circle
                    cx="50"
                    cy="50"
                    r="42"
                    stroke="#2563eb"
                    strokeWidth="12"
                    fill="none"
                    strokeDasharray={`${(case_strength_report?.score || 0) * 2.64} 264`}
                    strokeLinecap="round"
                  />
                </svg>
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="text-3xl font-bold text-slate-800">{case_strength_report?.score || 0}</span>
                </div>
              </div>
              <div>
                <p className="text-lg font-semibold text-slate-800">
                  {(case_strength_report?.score || 0) >= 90 ? 'Excellent' :
                   (case_strength_report?.score || 0) >= 75 ? 'Strong' :
                   (case_strength_report?.score || 0) >= 60 ? 'Moderate' : 'Needs Improvement'}
                </p>
                <p className="text-slate-500">Overall Case Strength</p>
              </div>
            </div>

            {/* Stats Grid */}
            <div className="grid grid-cols-2 gap-4">
              <div className="p-4 bg-slate-50 rounded-xl">
                <p className="text-sm text-slate-500">Evidence Completeness</p>
                <p className="text-lg font-bold text-slate-800">
                  {((case_strength_report?.evidence_completeness || 0) * 100).toFixed(0)}%
                </p>
              </div>
              <div className="p-4 bg-slate-50 rounded-xl">
                <p className="text-sm text-slate-500">Vulnerabilities Addressed</p>
                <p className="text-lg font-bold text-slate-800">
                  {case_strength_report?.vulnerabilities_addressed || 0} / {case_strength_report?.vulnerabilities_total || 0}
                </p>
              </div>
            </div>

            {case_strength_report?.blocking_issues && case_strength_report.blocking_issues.length > 0 && (
              <div className="p-4 bg-amber-50 rounded-xl border border-amber-200">
                <p className="font-medium text-amber-800 mb-2">Blocking Issues</p>
                <ul className="space-y-1">
                  {case_strength_report.blocking_issues.map((issue, index) => (
                    <li key={index} className="text-amber-700 flex items-start gap-2">
                      <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                      {issue}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </Section>

        {/* Defense Matrix */}
        <Section
          title="Defense Matrix"
          icon={<Shield className="w-5 h-5" />}
          expanded={expandedSections.defense}
          onToggle={() => toggleSection('defense')}
        >
          {vulnerability_matrix.length > 0 ? (
            <div className="space-y-4">
              {vulnerability_matrix.map((item, index) => (
                <div key={index} className="border rounded-xl overflow-hidden">
                  <div className="p-4 bg-slate-50 border-b">
                    <p className="font-medium text-slate-800">
                      {typeof item.attack_vector === 'string' ? item.attack_vector : item.vulnerability?.title || `Vulnerability ${index + 1}`}
                    </p>
                  </div>
                  <div className="p-4 space-y-3">
                    {item.strategy_a && (
                      <StrategyCard
                        label="A"
                        strategy={item.strategy_a}
                        recommended={item.recommended === 'A'}
                      />
                    )}
                    {item.strategy_b && (
                      <StrategyCard
                        label="B"
                        strategy={item.strategy_b}
                        recommended={item.recommended === 'B'}
                      />
                    )}
                    {item.strategy_c && (
                      <StrategyCard
                        label="C"
                        strategy={item.strategy_c}
                        recommended={item.recommended === 'C'}
                      />
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-slate-500 text-center py-8">No defense points identified.</p>
          )}
        </Section>

        {/* Document Index */}
        <Section
          title={`Document Index (${document_index.length})`}
          icon={<FileText className="w-5 h-5" />}
          expanded={expandedSections.documents}
          onToggle={() => toggleSection('documents')}
        >
          {document_index.length > 0 ? (
            <div className="border rounded-xl divide-y">
              {document_index.map((doc, index) => (
                <div key={index} className="flex items-center justify-between p-3 hover:bg-slate-50">
                  <div className="flex items-center gap-3">
                    <FileText className="w-5 h-5 text-slate-400" />
                    <div>
                      <p className="font-medium text-slate-700">{doc.filename}</p>
                      <p className="text-sm text-slate-500">{doc.doc_type}</p>
                    </div>
                  </div>
                  <div className="text-right text-sm text-slate-500">
                    {doc.page_count && <p>{doc.page_count} pages</p>}
                    <p>{formatDate(doc.uploaded_at)}</p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-slate-500 text-center py-8">No documents indexed.</p>
          )}
        </Section>

        {/* Methodology Notes */}
        <Section
          title="Methodology Notes"
          icon={<Users className="w-5 h-5" />}
          expanded={expandedSections.methodology}
          onToggle={() => toggleSection('methodology')}
        >
          <div className="prose prose-slate prose-sm max-w-none">
            {packageData.methodology_notes.split('\n').map((line, index) => (
              <p key={index} className="text-slate-600">{line}</p>
            ))}
          </div>
        </Section>

        {/* Footer */}
        <div className="mt-8 text-center text-sm text-slate-500">
          <p>Package ID: {packageData.id}</p>
          <p>Generated: {formatDate(packageData.generated_at)} • Version {packageData.version}</p>
          <p className="mt-2">© {new Date().getFullYear()} ClaimSync. All rights reserved.</p>
        </div>
      </main>
    </div>
  );
}

// Collapsible Section Component
interface SectionProps {
  title: string;
  icon: React.ReactNode;
  expanded: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}

function Section({ title, icon, expanded, onToggle, children }: SectionProps) {
  return (
    <div className="bg-white rounded-2xl shadow-sm mb-4 overflow-hidden">
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between p-4 hover:bg-slate-50 transition"
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center text-blue-600">
            {icon}
          </div>
          <span className="font-semibold text-slate-800">{title}</span>
        </div>
        {expanded ? (
          <ChevronDown className="w-5 h-5 text-slate-400" />
        ) : (
          <ChevronRight className="w-5 h-5 text-slate-400" />
        )}
      </button>
      {expanded && (
        <div className="px-4 pb-4">
          {children}
        </div>
      )}
    </div>
  );
}

// Strategy Card Component
interface StrategyCardProps {
  label: string;
  strategy: {
    name?: string;
    description?: string;
    strength?: number;
    risk_level?: string;
    counter_arguments?: string[];
  };
  recommended: boolean;
}

function StrategyCard({ label, strategy, recommended }: StrategyCardProps) {
  return (
    <div className={`p-3 rounded-lg border ${recommended ? 'border-blue-300 bg-blue-50' : 'border-slate-200'}`}>
      <div className="flex items-center gap-2 mb-2">
        <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
          recommended ? 'bg-blue-600 text-white' : 'bg-slate-200 text-slate-600'
        }`}>
          {label}
        </span>
        <span className="font-medium text-slate-800">{strategy.name || `Strategy ${label}`}</span>
        {recommended && (
          <span className="ml-auto text-xs bg-blue-600 text-white px-2 py-0.5 rounded-full">
            Recommended
          </span>
        )}
      </div>
      <p className="text-sm text-slate-600">{strategy.description || 'No description available.'}</p>
      {strategy.counter_arguments && strategy.counter_arguments.length > 0 && (
        <div className="mt-2 text-sm">
          <p className="text-slate-500 font-medium">Counter-argument:</p>
          <p className="text-slate-600">{strategy.counter_arguments[0]}</p>
        </div>
      )}
      <div className="mt-2 flex items-center gap-4 text-xs text-slate-500">
        {strategy.strength !== undefined && (
          <span>Strength: {strategy.strength}/10</span>
        )}
        {strategy.risk_level && (
          <span className={`capitalize ${
            strategy.risk_level === 'low' ? 'text-green-600' :
            strategy.risk_level === 'high' ? 'text-red-600' : 'text-amber-600'
          }`}>
            {strategy.risk_level} risk
          </span>
        )}
      </div>
    </div>
  );
}
